import { ReactNode, useState } from 'react'

// Reusable, non-functional settings controls for the wireframe prototype. Toggles
// hold local state so the screens feel real in stakeholder review, but nothing is
// wired to data. Used by the profile settings wireframe (and reusable for team/org).

// Small badge for flagging a setting (e.g. "Admin", "YouTube only", "Deprecated").
export function Tag({ label, tone = 'gray' }: { label: string; tone?: 'gray' | 'admin' | 'warn' }) {
    const styles = {
        gray: 'bg-gray-100 text-gray-800',
        admin: 'bg-arwen-50 text-arwen-700',
        warn: 'bg-suspect/15 text-suspect'
    }[tone]
    return (
        <span className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles}`}>
            {label}
        </span>
    )
}

// The admin-only marker, shared because most settings tabs flag several rows with
// it and they must all read identically.
export const ADMIN_TAG = <Tag label="Admin" tone="admin" />

// A faux switch that toggles its own local state — visual only.
export function FauxToggle({ defaultOn = false, disabled = false }: { defaultOn?: boolean; disabled?: boolean }) {
    const [on, setOn] = useState(defaultOn)
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled}
            onClick={() => setOn((o) => !o)}
            className={`relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors ${
                on ? 'bg-arwen-600' : 'bg-gray-300'
            } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    on ? 'translate-x-4' : 'translate-x-0.5'
                }`}
            />
        </button>
    )
}

// A titled card grouping related settings.
export function SettingsCard({
    title,
    description,
    badges,
    children
}: {
    title: string
    description?: string
    badges?: ReactNode
    children: ReactNode
}) {
    return (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2.5">
                <div className="flex items-center text-[13px] font-semibold text-gray-900">
                    {title}
                    {badges}
                </div>
                {description && <p className="mt-1 text-xs leading-relaxed text-gray-800">{description}</p>}
            </div>
            <div className="divide-y divide-dashed divide-gray-100 px-4">{children}</div>
        </div>
    )
}

// A setting row with a label, optional help text and a trailing control.
function Row({
    label,
    description,
    badges,
    control
}: {
    label: string
    description?: string
    badges?: ReactNode
    control: ReactNode
}) {
    return (
        <div className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
                <div className="flex flex-wrap items-center text-sm font-medium text-gray-800">
                    {label}
                    {badges}
                </div>
                {description && <p className="mt-0.5 text-xs leading-relaxed text-gray-800">{description}</p>}
            </div>
            <div className="flex-none pt-0.5">{control}</div>
        </div>
    )
}

// A row whose control is a faux toggle.
export function ToggleRow({
    label,
    description,
    badges,
    defaultOn,
    disabled
}: {
    label: string
    description?: string
    badges?: ReactNode
    defaultOn?: boolean
    disabled?: boolean
}) {
    return (
        <Row
            label={label}
            description={description}
            badges={badges}
            control={<FauxToggle defaultOn={defaultOn} disabled={disabled} />}
        />
    )
}

// A row whose control is a button — for links to sub-pages or one-off actions.
export function ActionRow({
    label,
    description,
    badges,
    action,
    danger
}: {
    label: string
    description?: string
    badges?: ReactNode
    action: string
    danger?: boolean
}) {
    return (
        <Row
            label={label}
            description={description}
            badges={badges}
            control={
                <button
                    type="button"
                    className={`rounded border px-2.5 py-1 text-xs font-semibold ${
                        danger
                            ? 'border-severe/40 text-severe hover:bg-severe/5'
                            : 'border-gray-200 text-arwen-700 hover:bg-gray-50'
                    }`}
                >
                    {action}
                </button>
            }
        />
    )
}

// A faux dropdown — visual only, shows a current value.
export function FauxSelect({ value }: { value: string }) {
    return (
        <span className="inline-flex items-center gap-2 rounded border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-800">
            {value}
            <span className="text-gray-400" aria-hidden="true">▾</span>
        </span>
    )
}
